<template>
  <div class="app-layout">
    <AppHeader />
    <main class="main-content">
      <router-view />
    </main>
    <footer class="site-footer">
      <div class="container footer-grid">
        <div class="footer-brand">
          <div class="logo-row">
            <span class="logo-mark">万</span>
            <strong>{{ t('brand.name') }}</strong>
          </div>
          <p>{{ t('footer.tagline') }}</p>
        </div>
        <div>
          <h4>{{ t('footer.quickLinks') }}</h4>
          <router-link to="/services">{{ t('footer.services') }}</router-link>
          <router-link to="/projects">{{ t('footer.projects') }}</router-link>
          <router-link to="/news">{{ t('footer.news') }}</router-link>
          <router-link to="/contact">{{ t('footer.contact') }}</router-link>
        </div>
        <div>
          <h4>{{ t('footer.contactInfo') }}</h4>
          <p>{{ t('footer.email') }}: {{ contact.email }}</p>
          <p>{{ t('footer.phone') }}: {{ contact.phone }}</p>
          <p>{{ t('footer.workHours') }}: {{ contact.work_hours }}</p>
          <p>{{ t('contact.addressLabel') }}: {{ contact.address }}</p>
        </div>
      </div>
      <div class="footer-bottom">
        <div class="container">
          <span>{{ t('footer.copyright', { year }) }}</span>
        </div>
      </div>
    </footer>
    <Toast ref="toastRef" />
  </div>
</template>

<script>
import { ref, provide, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppHeader from './AppHeader.vue'
import Toast from '../Toast.vue'
import { getContactInfo } from '../../api'
import { localizedContact } from '../../utils/i18nContent'
import { autoTranslateBatch } from '../../utils/autoTranslate'

export default {
  name: 'AppLayout',
  components: { AppHeader, Toast },
  setup() {
    const { t, locale } = useI18n()
    const toastRef = ref(null)
    const apiContact = ref(null)
    const contact = ref(localizedContact(null, locale.value, t))
    const year = new Date().getFullYear()

    async function syncContact() {
      const base = localizedContact(apiContact.value, locale.value, t)
      if (locale.value === 'zh') {
        contact.value = base
        return
      }

      const fields = []
      const keys = []
      if (base.address) {
        fields.push(base.address)
        keys.push('address')
      }
      if (base.work_hours) {
        fields.push(base.work_hours)
        keys.push('work_hours')
      }

      if (!fields.length) {
        contact.value = base
        return
      }

      try {
        const translated = await autoTranslateBatch(fields, locale.value)
        const next = { ...base }
        keys.forEach((key, index) => {
          next[key] = translated[index] || base[key]
        })
        contact.value = next
      } catch {
        contact.value = base
      }
    }

    async function reloadContact() {
      try {
        const res = await getContactInfo()
        apiContact.value = res.data
      } catch {
        apiContact.value = null
      }
      await syncContact()
    }

    watch([apiContact, locale], syncContact, { immediate: true })

    provide('toast', {
      success: (msg) => toastRef.value?.show(msg, 'success'),
      error: (msg) => toastRef.value?.show(msg, 'error'),
    })
    provide('apiContact', apiContact)
    provide('reloadContact', reloadContact)

    onMounted(reloadContact)

    return { toastRef, t, contact, year }
  },
}
</script>

<style scoped>
.app-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
}

.site-footer {
  background: var(--color-primary-dark);
  color: rgba(255, 255, 255, 0.85);
  margin-top: auto;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 2.5rem;
  padding: 3.5rem 1.25rem;
}

.footer-brand p {
  margin: 1rem 0 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.92rem;
  max-width: 320px;
}

.logo-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.logo-mark {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--color-accent);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.site-footer h4 {
  color: var(--color-accent);
  margin: 0 0 1rem;
  font-size: 0.95rem;
}

.site-footer a,
.site-footer p {
  display: block;
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
}

.site-footer a:hover {
  color: #fff;
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.25rem 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
}

@media (max-width: 768px) {
  .footer-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
}
</style>
